import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Select, Row } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { AButton, ARate, ATextArea } from '../../../../components';
import {
  IProfessional,
  IDentalTreatmentItem,
} from '../../../../shared/interfaces';
import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';

interface IDentalEditEvolutionProps {
  open: boolean;
  close: () => void;
  professionals: IProfessional[];
  type?: number;
  treatmentItem: IDentalTreatmentItem | null;
  updateProfessional: boolean;
  setTreatmentItems: React.Dispatch<
    React.SetStateAction<IDentalTreatmentItem[]>
  >;
}

const EditEvolution: React.FC<IDentalEditEvolutionProps> = ({
  open,
  close,
  professionals,
  type,
  treatmentItem,
  updateProfessional,
  setTreatmentItems,
}) => {
  const [form] = Form.useForm();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [rank, setRank] = useState<number>(treatmentItem?.rank || 0);
  const [observation, setObservation] = useState<string>();

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (updateProfessional) {
        await api
          .patch(`/dental-treatments-items/${treatmentItem?.id}`, {
            professional_id: values.professional_id,
          })
          .then((response) => {
            setTreatmentItems((state) =>
              state.map((e) => (e.id !== response.data.id ? e : response.data)),
            );

            addToast({
              type: 'success',
              title: 'Sucesso',
              description: 'Profissional alterado com sucesso!',
            });
          })
          .catch((erro) => {
            console.error('Erro ao atualizar profissional:', erro);
            addToast({
              type: 'error',
              title: 'Erro',
              description:
                erro.response?.data?.message ||
                'Erro ao atualizar profissional',
            });
          });
      } else {
        await api
          .patch(`/dental-treatments-items/${treatmentItem?.id}`, {
            dental_treatment_id: treatmentItem?.dental_treatment_id,
            procedure_id: treatmentItem?.procedure_id,
            professional_id: treatmentItem?.professional_id,
            status: type,
            rank,
            observation,
          })
          .then((response) => {
            setTreatmentItems((state) =>
              state.map((e) => (e.id !== response.data.id ? e : response.data)),
            );

            addToast({
              type: 'success',
              title: 'Sucesso',
              description: 'Status alterado com sucesso!',
            });
          })
          .catch((erro) => {
            console.error('Erro ao atualizar status:', erro);
            addToast({
              type: 'error',
              title: 'Erro',
              description:
                erro.response?.data?.message || 'Erro ao atualizar status',
            });
          });
      }
      close();
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description:
          err.response?.data?.message ||
          'Ocorreu um erro ao salvar as alterações.',
      });
    } finally {
      setLoading(false);
    }
  }, [
    form,
    updateProfessional,
    treatmentItem,
    type,
    rank,
    observation,
    setTreatmentItems,
    addToast,
    close,
  ]);

  useEffect(() => {
    if (open && treatmentItem) {
      form.setFieldsValue({
        professional_id: treatmentItem.professional_id,
      });
      setRank(treatmentItem.rank || 0);
      setObservation(treatmentItem.observation || '');
    }
  }, [open, treatmentItem, form]);

  return (
    <Modal
      title={updateProfessional ? 'Alterar Profissional' : 'Alterar Status'}
      open={open}
      onCancel={close}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {updateProfessional ? (
          <Form.Item
            name="professional_id"
            label="Profissional"
            rules={[{ required: true, message: 'Selecione um profissional' }]}
          >
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={professionals.map((p) => ({
                label: p.name,
                value: p.id,
              }))}
            />
          </Form.Item>
        ) : (
          <>
            <p>
              Deseja realmente alterar o status para{' '}
              <strong>{type === 1 ? 'Em andamento' : 'Concluído'}</strong>?
            </p>

            <Form.Item label="Observações">
              <ATextArea
                rows={5}
                placeholder="descreva suas observações"
                name="observation"
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                style={{ resize: 'none' }}
              />
            </Form.Item>
            <Form.Item label="Avaliação da Evolução">
              <ARate
                value={rank}
                onChange={(e) => setRank(e)}
                tooltips={['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']}
                count={10}
              />
            </Form.Item>
          </>
        )}

        <Row justify="end">
          <AButton
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<SaveOutlined />}
          >
            Salvar
          </AButton>
        </Row>
      </Form>
    </Modal>
  );
};

export default EditEvolution;
